import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CreditCardIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CogIcon,
  DocumentTextIcon,
  QrCodeIcon,
  BuildingLibraryIcon,
  LinkIcon
} from '@heroicons/react/24/outline'
import { accountsAPI, paymentGatewayAPI } from '@/services/api'

interface GatewayAccount {
  id: number
  name: string
  bank: string
  balance: number
  is_active: boolean
}

interface GatewayStats {
  total_balance: number
  total_transactions: number
  total_income: number
  total_expense: number
  accounts_count: number
}

const PaymentGatewayDashboard: React.FC = () => {
  const navigate = useNavigate()
  const [gatewayAccounts, setGatewayAccounts] = useState<GatewayAccount[]>([])
  const [stats, setStats] = useState<GatewayStats>({
    total_balance: 0,
    total_transactions: 0,
    total_income: 0,
    total_expense: 0,
    accounts_count: 0
  })
  const [integrations, setIntegrations] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        setLoading(true)

        // Buscar todas as contas usando o método correto
        const allAccounts = await accountsAPI.list()

        // Filtrar apenas contas de gateway (ASAAS, PAGARME, STRIPE, FINPRO, HUBNAI)
        const gatewayAccs = allAccounts.filter((acc: any) =>
          acc.bank && ['ASAAS', 'PAGARME', 'STRIPE', 'FINPRO', 'HUBNAI'].includes(acc.bank.toUpperCase())
        )

        setGatewayAccounts(gatewayAccs)

        // Calcular estatísticas
        const totalBalance = gatewayAccs.reduce((sum: number, acc: any) => sum + parseFloat(acc.balance || 0), 0)

        // Buscar transações de todas as contas de gateway
        let totalIncome = 0
        let totalExpense = 0
        let totalTransactions = 0

        for (const acc of gatewayAccs) {
          try {
            const txResponse = await accountsAPI.getTransactions(acc.id, { limit: 1000 })
            const transactions = txResponse.transactions || txResponse || []
            totalTransactions += transactions.length

            transactions.forEach((tx: any) => {
              if (tx.type === 'INCOME') {
                totalIncome += parseFloat(tx.amount || 0)
              } else {
                totalExpense += parseFloat(tx.amount || 0)
              }
            })
          } catch (err) {
            console.error(`Error fetching transactions for account ${acc.id}:`, err)
          }
        }

        setStats({
          total_balance: totalBalance,
          total_transactions: totalTransactions,
          total_income: totalIncome,
          total_expense: totalExpense,
          accounts_count: gatewayAccs.length
        })

        // Buscar gateways de pagamento configurados
        try {
          const gatewaysResponse = await paymentGatewayAPI.getGateways()
          setIntegrations(gatewaysResponse || [])
        } catch (err) {
          console.error('Error fetching payment gateways:', err)
          setIntegrations([])
        }

        setError(null)
      } catch (err: any) {
        console.error('Payment Gateway Dashboard Error:', err)
        setError('Erro ao carregar dados do dashboard')
      } finally {
        setLoading(false)
      }
    }

    fetchPaymentData()
  }, [])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'inactive': return 'bg-gray-100 text-gray-800'
      case 'error': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'pix': return <QrCodeIcon className="w-5 h-5" />
      case 'credit_card':
      case 'debit_card': return <CreditCardIcon className="w-5 h-5" />
      case 'boleto': return <DocumentTextIcon className="w-5 h-5" />
      default: return <CurrencyDollarIcon className="w-5 h-5" />
    }
  }

  const getGatewayIcon = (type: string) => {
    switch (type) {
      case 'asaas': return <BuildingLibraryIcon className="w-6 h-6" />
      case 'pagbank': return <BanknotesIcon className="w-6 h-6" />
      case 'stripe': return <CreditCardIcon className="w-6 h-6" />
      case 'mercadopago': return <CurrencyDollarIcon className="w-6 h-6" />
      case 'finpro': return <QrCodeIcon className="w-6 h-6 text-orange-600" />
      case 'hubnai': return <QrCodeIcon className="w-6 h-6 text-orange-600" />
      default: return <CreditCardIcon className="w-6 h-6" />
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar Payment Gateway</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCardIcon className="w-7 h-7 text-blue-600" />
            Payment Gateways
          </h1>
          <p className="text-gray-600">Gestão de meios de pagamento e reconciliação</p>
        </div>
        <button
          onClick={() => navigate('/integrations')}
          className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-2 rounded-lg hover:from-emerald-700 hover:to-teal-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <LinkIcon className="w-5 h-5" />
          Gerenciar Gateways em Integrações
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Saldo Total</p>
              <p className="text-2xl font-bold">{formatCurrency(stats.total_balance)}</p>
            </div>
            <BanknotesIcon className="w-8 h-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Receitas Totais</p>
              <p className="text-2xl font-bold">{formatCurrency(stats.total_income)}</p>
            </div>
            <ArrowTrendingUpIcon className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Despesas Totais</p>
              <p className="text-2xl font-bold">{formatCurrency(stats.total_expense)}</p>
            </div>
            <ArrowTrendingDownIcon className="w-8 h-8 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Total de Transações</p>
              <p className="text-2xl font-bold">{stats.total_transactions}</p>
            </div>
            <ChartBarIcon className="w-8 h-8 text-orange-200" />
          </div>
        </div>
      </div>

      {/* Gateway Cards - Quick Access */}
      {integrations.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Acesso Rápido aos Gateways</h3>
            <span className="text-sm text-gray-500">{integrations.length} gateway(s) configurado(s)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrations.map((gateway: any) => {
              const relatedAccounts = gatewayAccounts.filter(acc =>
                acc.bank?.toUpperCase() === gateway.gateway_type?.toUpperCase()
              )
              const totalBalance = relatedAccounts.reduce((sum, acc) => sum + parseFloat(acc.balance || '0'), 0)

              return (
                <div key={gateway.id} className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 hover:border-blue-400 p-6 shadow-sm hover:shadow-xl transition-all duration-300 group">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl ${gateway.is_active ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gray-300'} shadow-lg`}>
                        <div className="text-white">
                          {getGatewayIcon(gateway.gateway_type)}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg">{gateway.name}</h4>
                        <p className="text-sm text-gray-500 capitalize">{gateway.gateway_type}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${gateway.is_active ? 'bg-green-100 text-green-700 ring-2 ring-green-200' : 'bg-gray-100 text-gray-600'}`}>
                      {gateway.is_active ? '● ATIVO' : '○ INATIVO'}
                    </span>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Saldo Total</span>
                      <span className="text-lg font-bold text-gray-900">{formatCurrency(totalBalance)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Contas Vinculadas</span>
                      <span className="text-sm font-semibold text-blue-600">{relatedAccounts.length} conta(s)</span>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4 flex gap-2">
                    <button
                      onClick={() => {
                        const gatewayPath = gateway.gateway_type.toLowerCase()
                        navigate(`/integrations/${gatewayPath}/${gateway.id}`)
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg group-hover:scale-105"
                    >
                      <ChartBarIcon className="w-4 h-4" />
                      Dashboard
                    </button>
                    <button
                      onClick={() => navigate('/integrations')}
                      className="px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-all duration-200 font-medium"
                    >
                      <CogIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Contas de Gateway */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Contas de Gateway</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {gatewayAccounts.map((acc) => (
            <div key={acc.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/accounts/${acc.id}/statement`)}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BuildingLibraryIcon className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">{acc.name}</h4>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${acc.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {acc.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              <div className="text-sm text-gray-600 mb-2">{acc.bank}</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(acc.balance)}</div>
            </div>
          ))}
          {gatewayAccounts.length === 0 && (
            <div className="col-span-3 text-center py-8 text-gray-500">
              Nenhuma conta de gateway configurada
            </div>
          )}
        </div>
      </div>

      {/* Gateway Status */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Gateways Configurados</h3>
        </div>

        {integrations.length === 0 ? (
          <div className="p-12 text-center">
            <BuildingLibraryIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum Gateway Configurado</h3>
            <p className="text-gray-500 mb-6">Configure um gateway de pagamento para começar a processar transações</p>
            <button
              onClick={() => navigate('/integrations')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Configurar Gateway
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gateway</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contas Vinculadas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Saldo Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Última Atualização</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {integrations.map((gateway: any) => {
                  const relatedAccounts = gatewayAccounts.filter(acc =>
                    acc.bank?.toUpperCase() === gateway.gateway_type?.toUpperCase()
                  )
                  const totalBalance = relatedAccounts.reduce((sum, acc) => sum + parseFloat(acc.balance || '0'), 0)

                  return (
                    <tr key={gateway.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              {getGatewayIcon(gateway.gateway_type)}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{gateway.name}</div>
                            <div className="text-sm text-gray-500 capitalize">{gateway.gateway_type}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${gateway.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {gateway.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{relatedAccounts.length} conta(s)</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(totalBalance)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {gateway.updated_at ? new Date(gateway.updated_at).toLocaleDateString('pt-BR') : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              // Navegar para o dashboard específico do gateway
                              const gatewayPath = gateway.gateway_type.toLowerCase()
                              navigate(`/integrations/${gatewayPath}/${gateway.id}`)
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium text-sm shadow-sm hover:shadow-md"
                          >
                            <ChartBarIcon className="w-4 h-4" />
                            Ver Dashboard
                          </button>
                          <button
                            onClick={() => navigate('/integrations')}
                            className="text-emerald-600 hover:text-emerald-900 font-medium"
                          >
                            Gerenciar
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {/* Quick Access Tools */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          onClick={() => navigate('/payments/pix-transfer')}
          className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <QrCodeIcon className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Transferência PIX</h3>
          </div>
          <p className="text-gray-600 mb-4">Faça transferências PIX direto pela plataforma</p>
          <button className="text-blue-600 font-medium hover:text-blue-700">
            Fazer Transferência →
          </button>
        </div>

        <div
          onClick={() => navigate('/payments/pix')}
          className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <QrCodeIcon className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Gestão PIX</h3>
          </div>
          <p className="text-gray-600 mb-4">Gerencie chaves PIX e cobranças</p>
          <button className="text-green-600 font-medium hover:text-green-700">
            Gerenciar PIX →
          </button>
        </div>

        <div
          onClick={() => navigate('/bank-statements')}
          className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BuildingLibraryIcon className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Reconciliação Bancária</h3>
          </div>
          <p className="text-gray-600 mb-4">Automatize a reconciliação com extratos bancários</p>
          <button className="text-purple-600 font-medium hover:text-purple-700">
            Acessar Reconciliação →
          </button>
        </div>
      </div>

    </div>
  )
}

export default PaymentGatewayDashboard